import { Link, useParams } from "react-router-dom";
import Seo from "@/lib/Seo";
import { useFetch } from "@/lib/api";
import { FadeUp } from "@/lib/motion";
import { useQuote } from "@/lib/quote";
import Breadcrumbs from "@/components/Breadcrumbs";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { data: service, loading, error } = useFetch(`/services/${slug}`);
  const { setAtelierOpen } = useQuote();

  if (loading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-[72px]">
        <p className="text-smoke" data-testid="service-loading">Opening the service…</p>
      </div>
    );
  }
  if (error || !service) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6 px-6 pt-[72px] text-center">
        <h1 className="font-display text-4xl text-ink">We could not find that service.</h1>
        <Link to="/services" data-testid="service-back-link" className="rounded-none bg-olive px-8 py-3 text-[12px] uppercase tracking-[0.18em] text-ivory">
          All Services
        </Link>
      </div>
    );
  }

  const comingSoon = service.status === "coming_soon";

  return (
    <div className="pt-[72px]">
      <Seo
        title={`${service.name} — Southern California Wedding Service | Marble & Veil`}
        description={service.short.slice(0, 155)}
        path={`/services/${service.slug}`}
      />
      <section className="mx-auto max-w-7xl px-5 py-12 sm:px-8 lg:py-20">
        <Breadcrumbs trail={[["Home", "/"], ["Services", "/services"], [service.name]]} />
        <div className="mt-10 grid gap-12 lg:grid-cols-2 lg:gap-16">
          <FadeUp>
            <div className="overflow-hidden bg-parch">
              <img src={service.image} alt={`${service.name} — design inspiration`} className="aspect-[4/3] w-full object-cover" width="900" height="675" data-testid="service-hero-image" />
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="lg:sticky lg:top-28">
              {comingSoon && (
                <span className="inline-block border border-olive/50 px-3 py-1 text-[10px] uppercase tracking-widest2 text-olive" data-testid="service-coming-soon-badge">
                  Coming soon — cannot be booked yet
                </span>
              )}
              <h1 className="mt-3 font-display text-4xl leading-[1.08] text-ink sm:text-5xl" data-testid="service-title">{service.name}</h1>
              <p className="mt-3 font-display text-2xl italic text-olive">{service.tagline}</p>
              {service.description.map((p, i) => (
                <p key={i} className="mt-5 leading-relaxed text-smoke">{p}</p>
              ))}

              <div className="mt-8 border-t border-ink/10 pt-6">
                <p className="mv-label">What we will ask about</p>
                <ul className="mt-3 space-y-1.5 text-sm text-smoke" data-testid="service-asks">
                  {(service.asks || []).map((a) => (
                    <li key={a} className="flex items-baseline gap-3">
                      <span className="h-px w-5 flex-none bg-olive" aria-hidden="true" /> {a}
                    </li>
                  ))}
                </ul>
              </div>

              <p className="mt-6 text-[12px] leading-relaxed text-smoke" data-testid="service-note">{service.note}</p>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/quote"
                  data-testid="service-availability-cta"
                  className="rounded-none bg-olive px-10 py-4 text-[12px] uppercase tracking-[0.18em] text-ivory transition-transform duration-300 hover:-translate-y-0.5"
                >
                  {comingSoon ? "Ask About Future Availability" : "Request Availability"}
                </Link>
                <button data-testid="service-atelier-cta" onClick={() => setAtelierOpen(true)} className="border border-ink/25 px-10 py-4 text-[12px] uppercase tracking-[0.18em] text-ink transition-colors hover:border-olive hover:text-olive">
                  Open the Palette Atelier
                </button>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
